import { Modal } from '@sqs/rosetta-elements';
import { Box, Button, Flex, Text } from '@sqs/rosetta-primitives';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { addUserToSchedulingInstance } from '../../../apis/schedulingInstances';
import { usePlatformBreakpoint } from '../../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../../i18n';
import { SchedulingInstancePermission, SchedulingInstancePermissionData } from '../../../models/SchedulingInstance';
import { ORG_MANAGER } from '../../../models/SqspUser';
import { selectEnterprise } from '../../../stores/currentUser';
import { sendErrorMessage, sendSuccessMessage } from '../../../stores/messages';
import { fetchOrgUsers, selectAllOrgUsers } from '../../../stores/orgUsers';
import { RootState } from '../../../stores/rootReducer';
import { useAppDispatch, useAppSelector } from '../../../stores/store';
import { returnCheckedItemIds } from '../../../utils/searchSelectUtils';
import { DropdownSearch, DropdownSearchOption } from '../../common/DropdownSearch';
import { GenericModal } from '../../common/GenericModal';
import { ModalTitle } from '../../common/ModalTitle';
import { AddUserToInstanceForm } from '../../common/types/UserInstanceForm';
import { SetUserPermissions } from '../../common/UserPermissions/SetUserPermissions';

interface AddUserToInstanceModalProps {
  readonly closeModal: () => void;
  readonly isOpen: boolean;
  readonly schedulingInstanceId: number;
}

interface AddUserToInstanceModalForm extends AddUserToInstanceForm {
  userId: number;
}

export const AddUserToInstanceModal = ({ closeModal, isOpen, schedulingInstanceId }: AddUserToInstanceModalProps) => {
  const { T, t } = useI18n();
  const { isMobile } = usePlatformBreakpoint();
  const dispatch = useAppDispatch();

  const { hasLoaded, users, schedulingInstanceUserIds, enterpriseId } = useAppSelector((state: RootState) => {
    return {
      enterpriseId: selectEnterprise(state.currentUser)?.id,
      hasLoaded: state.orgUsers && !state.orgUsers.isLoading,
      schedulingInstanceUserIds: state.schedulingInstanceUsers.ids,
      users: selectAllOrgUsers(state.orgUsers)
    };
  });

  useEffect(() => {
    if (isOpen && !hasLoaded) {
      if (!enterpriseId) {
        throw new Error('User is not connected to an Enterprise');
      }

      dispatch(fetchOrgUsers(enterpriseId));
    }
  }, [hasLoaded, isOpen, users, enterpriseId, dispatch]);

  const formMethods = useForm<AddUserToInstanceModalForm>({
    defaultValues: {
      schedulingInstanceId,
      items: []
    }
  });
  const { handleSubmit, reset, setValue, watch } = formMethods;

  const userId = watch('userId');

  const triggerCloseModal = () => {
    reset();
    closeModal();
  };

  const sendAddUserToInstance = handleSubmit((formData) => {
    const calendars = !formData.allItems ? returnCheckedItemIds(formData.items) : [];

    const data: SchedulingInstancePermissionData = {
      permissions: formData.permission as SchedulingInstancePermission,
      allowExports: Boolean(formData.allowExports),
      calendars: calendars || [],
      allCalendars: formData.allItems
    };
    if (enterpriseId) {
      addUserToSchedulingInstance(enterpriseId, schedulingInstanceId, formData.userId, data).
      then(() => {
        dispatch(sendSuccessMessage(t("User was added to the instance", {}, { project: 'enterprise-dashboard' })));
        dispatch(fetchOrgUsers(enterpriseId));
        triggerCloseModal();
      }).
      catch(() => {
        dispatch(
          sendErrorMessage(t("There was a problem adding the user to the scheduling instance",
          null, {
            project: 'enterprise-dashboard' })

          )
        );
      });
    }
  });

  if (!hasLoaded) {
    return null;
  }

  // Filter out Org Managers who will already have access to every instance
  // Filter out users already assigned to the scheduling instance
  const dropdownSearchUserList: DropdownSearchOption[] = users.
  filter((u) => u.role !== ORG_MANAGER && !schedulingInstanceUserIds.includes(u.id)).
  map((u) => ({ value: u.id, label: u.name, additionalSearch: [u.email], displayOnSelect: u.email }));

  const disableSaveButton = !userId || !watch('permission');

  return (
    <Modal.Transition>
      {isOpen &&
      <GenericModal
        closeModal={triggerCloseModal}
        modalActions={
        <Flex justifyContent="space-between" mt={isMobile ? 0 : 6}>
              <Button.Tertiary type="button" size="medium" onClick={triggerCloseModal}>
                <T project="enterprise-dashboard">{"Cancel"}</T>
              </Button.Tertiary>
              <Button.Primary size="medium" type="submit" onClick={sendAddUserToInstance} disabled={disableSaveButton}>
                <T project="enterprise-dashboard">{"Add"}</T>
              </Button.Primary>
            </Flex>
        }>

          <FormProvider {...formMethods}>
            <ModalTitle>
              <T project="enterprise-dashboard">{"Add user to instance"}</T>
            </ModalTitle>
            <Box mt={3}>
              <Text.Label>
                <T project="enterprise-dashboard">{"Email Address"}</T>
                <DropdownSearch
                dropdownSearchOptions={dropdownSearchUserList}
                value={userId}
                onChange={(v: number) => setValue('userId', v, { shouldDirty: true })}
                emptyMessageOptions={{
                  empty: t("There are no users available to add to this instance.",

                  {}, {
                    project: 'enterprise-dashboard' }),

                  match: t("No users match your search.", {}, { project: 'enterprise-dashboard' })
                }} />

              </Text.Label>
            </Box>
            <SetUserPermissions />
          </FormProvider>
        </GenericModal>
      }
    </Modal.Transition>);

};